"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import { getCanonicalNextMintNumber, isMintingToday } from "@/components/meme-calendar/meme-calendar.helpers";
import { MEMES_CONTRACT } from "@/constants/constants";
import { shouldHideSubscriptions } from "@/components/user/layout/userPageVisibility";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { ApiUpcomingMemeSubscriptionStatus } from "@/generated/models/ApiUpcomingMemeSubscriptionStatus";
import type { NFTSubscription } from "@/generated/models/NFTSubscription";
import type { SubscriptionDetails } from "@/generated/models/SubscriptionDetails";
import useCapacitor from "@/hooks/useCapacitor";
import { commonApiFetch } from "@/services/api/common-api";
import { useQuery } from "@tanstack/react-query";
import { useContext, useMemo } from "react";
import MemeSubscriptionRow from "../../user/subscriptions/MemeSubscriptionRow";
function getProfileKey(connectedProfile: ApiIdentity | null): string | undefined {
  return connectedProfile?.consolidation_key ?? connectedProfile?.wallets?.map(wallet => wallet.wallet).join("-");
}
export default function LatestDropNextMintSubscribe() {
  const $ = _c(33);
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    country
  } = useCookieConsent();
  const {
    isIos
  } = useCapacitor();
  const tokenId = getCanonicalNextMintNumber();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = Number.isInteger(tokenId) && tokenId > 0;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const hasTokenId = t0;
  let t1;
  if ($[1] !== country || $[2] !== isIos) {
    t1 = shouldHideSubscriptions({
      capacitorIsIos: isIos,
      country
    });
    $[1] = country;
    $[2] = isIos;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const hideSubscriptions = t1;
  let t2;
  if ($[4] !== activeProfileProxy || $[5] !== connectedProfile) {
    t2 = activeProfileProxy ? undefined : getProfileKey(connectedProfile);
    $[4] = activeProfileProxy;
    $[5] = connectedProfile;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const profileKey = t2;
  let t3;
  let t4;
  if ($[7] !== profileKey) {
    t3 = ["next-mint-subscription-details", profileKey];
    t4 = async () => await commonApiFetch({
      endpoint: `subscriptions/consolidation/details/${profileKey}`
    });
    $[7] = profileKey;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  const t5 = !hideSubscriptions && !!profileKey;
  let t6;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    data: details
  } = useQuery(t6);
  let t7;
  let t8;
  if ($[14] !== profileKey) {
    t7 = ["next-mint-subscription-status", profileKey, tokenId];
    t8 = async () => await commonApiFetch({
      endpoint: `subscriptions/consolidation/upcoming-memes/${tokenId}/${profileKey}`
    });
    $[14] = profileKey;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  const t9 = !hideSubscriptions && !!profileKey && hasTokenId;
  let t10;
  if ($[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9
    };
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const {
    data: status,
    refetch: refetchStatus
  } = useQuery(t10);
  let t11;
  bb0: {
    if (!profileKey || !hasTokenId || !status) {
      t11 = null;
      break bb0;
    }
    const t12 = status.count ?? 1;
    let t13;
    if ($[21] !== profileKey || $[22] !== status.subscribed || $[23] !== t12) {
      t13 = {
        consolidation_key: profileKey,
        contract: MEMES_CONTRACT,
        token_id: tokenId,
        subscribed: status.subscribed,
        subscribed_count: t12
      };
      $[21] = profileKey;
      $[22] = status.subscribed;
      $[23] = t12;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    t11 = t13 as NFTSubscription;
  }
  const subscription = t11;
  if (hideSubscriptions || !profileKey || !subscription) {
    return null;
  }
  const t12 = details?.subscription_eligibility_count ?? status?.eligibility ?? 1;
  let t13;
  if ($[25] !== refetchStatus) {
    t13 = () => {
      refetchStatus();
    };
    $[25] = refetchStatus;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = isMintingToday();
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== profileKey || $[29] !== subscription || $[30] !== t12 || $[31] !== t13) {
    t15 = <div className="tw-mt-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-pt-4"><div className="tw-rounded-xl tw-bg-transparent"><MemeSubscriptionRow profileKey={profileKey} title="The Memes" subscription={subscription} eligibilityCount={t12} readonly={false} refresh={t13} minting_today={t14} first={true} date={null} variant="compact" /></div></div>;
    $[28] = profileKey;
    $[29] = subscription;
    $[30] = t12;
    $[31] = t13;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}